// src/services/trello.js
import { doc, getDoc } from "firebase/firestore";

let cachedConfig = null;
let cachedBoardId = null;

const getTrelloConfig = async (db, appId) => {
  if (cachedConfig) return cachedConfig;
  try {
    const docRef = doc(db, "artifacts", appId, "public", "trello_config");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      cachedConfig = docSnap.data();
      return cachedConfig;
    } else {
      throw new Error("找不到 Trello 設定檔");
    }
  } catch (error) {
    console.error("讀取 Trello 設定失敗:", error);
    throw error;
  }
};

const getBoardId = async (apiKey, token, listId) => {
  if (cachedBoardId) return cachedBoardId;
  const url = `https://api.trello.com/1/lists/${listId}?fields=idBoard&key=${apiKey}&token=${token}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Get Board ID Error: ${response.statusText}`);
  const data = await response.json();
  cachedBoardId = data.idBoard; 
  return data.idBoard;
};

// ==========================================================
// 主要功能
// ==========================================================

export const createTrelloCard = async (db, appId, cardTitle, cardDesc) => {
  const { apiKey, token, listId } = await getTrelloConfig(db, appId);
  const url = `https://api.trello.com/1/cards?key=${apiKey}&token=${token}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({
      idList: listId,
      name: cardTitle,
      desc: cardDesc,
      pos: 'top'
    })
  });
  if (!response.ok) throw new Error(`Create Error: ${response.statusText}`);
  return await response.json();
};

// ★★★ 核心修改：雙軌並行搜尋 (Hybrid Search) ★★★
// 1. Board API: 負責「即時」找到剛建立/使用中的卡片 (無延遲)
// 2. Search API: 負責找到「封存/歷史」卡片 (無數量限制)
export const searchBoardCards = async (db, appId, query) => {
  const { apiKey, token, listId } = await getTrelloConfig(db, appId);
  const boardId = await getBoardId(apiKey, token, listId);
  
  // 為了效能與準確度，我們同時發出兩個請求
  const [activeCardsResponse, searchResponse] = await Promise.all([
    // 請求 1: 抓取看板上所有「使用中 (Open)」的卡片 (解決: 剛建立搜尋不到的延遲問題)
    fetch(`https://api.trello.com/1/boards/${boardId}/cards?filter=open&fields=name,shortUrl,id,closed&limit=1000&key=${apiKey}&token=${token}`),
    
    // 請求 2: 使用 Trello 全域搜尋 (解決: 封存卡片被擠出 1000 筆限制的問題)
    // 我們限制 idBoards 只搜尋當前看板，並且搜尋 modelTypes=cards
    fetch(`https://api.trello.com/1/search?query=${encodeURIComponent(query)}&idBoards=${boardId}&modelTypes=cards&card_fields=name,shortUrl,id,closed&cards_limit=100&key=${apiKey}&token=${token}`)
  ]);

  if (!activeCardsResponse.ok) throw new Error(`Get Active Cards Error: ${activeCardsResponse.statusText}`);
  if (!searchResponse.ok) throw new Error(`Search API Error: ${searchResponse.statusText}`);
  
  const activeCards = await activeCardsResponse.json(); // 陣列
  const searchData = await searchResponse.json();       // 物件 { options:..., cards: [...] }
  const searchCards = searchData.cards || [];

  // 合併兩邊的結果
  const allCandidates = [...activeCards, ...searchCards];

  // 去除重複 (因為一張卡片可能同時出現在兩邊)
  const uniqueMap = new Map();
  allCandidates.forEach(card => {
    uniqueMap.set(card.id, card);
  });
  const uniqueCards = Array.from(uniqueMap.values());

  // 前端再次嚴格比對文字 (因為 Search API 有時候會模糊匹配)
  const lowerQuery = query.toLowerCase();
  const matchedCards = uniqueCards.filter(card => 
    card.name.toLowerCase().includes(lowerQuery)
  );
  
  return matchedCards;
};

export const addTrelloComment = async (db, appId, cardId, text) => {
  const { apiKey, token } = await getTrelloConfig(db, appId);
  const url = `https://api.trello.com/1/cards/${cardId}/actions/comments?text=${encodeURIComponent(text)}&key=${apiKey}&token=${token}`;
  const response = await fetch(url, { method: 'POST' });
  if (!response.ok) throw new Error(`Comment Error: ${response.statusText}`);
  return await response.json();
};

export const unarchiveTrelloCard = async (db, appId, cardId) => {
  const { apiKey, token } = await getTrelloConfig(db, appId);
  // 將 closed 設為 false 即為解封存
  const url = `https://api.trello.com/1/cards/${cardId}?closed=false&key=${apiKey}&token=${token}`;
  
  const response = await fetch(url, { method: 'PUT' });
  if (!response.ok) throw new Error(`Unarchive Error: ${response.statusText}`);
  return await response.json();
};